<script lang="ts">
    import Portrait from "$lib/components/Portrait.svelte";
    import TierBadge from "$lib/components/TierBadge.svelte";
    import {
        formatPercent,
        getTextColorClass,
        getPickRateColorClass,
        getBanRateColorClass,
        getStatRange,
        isInsufficientData,
    } from "$lib/utils/formatting";
    import { getRoleIcon } from "$lib/utils/images";
    import type { HeroStats } from "$lib/types";

    type SortKey = "name" | "role" | "winRate" | "pickRate" | "banRate" | "pickBan" | "tier";

    let {
        heroes,
        showRole = true,
        showBanRate = false,
        heroLinkSuffix = "",
        sortBy = $bindable<SortKey>("winRate"),
        sortDir = $bindable<"asc" | "desc">("desc"),
        onsortchange,
    }: {
        heroes: HeroStats[];
        showRole?: boolean;
        /** When true, render sortable Ban Rate and Pick+Ban columns. Hidden in Quick Play. */
        showBanRate?: boolean;
        heroLinkSuffix?: string;
        sortBy?: SortKey;
        sortDir?: "asc" | "desc";
        onsortchange?: () => void;
    } = $props();

    function toggleSort(field: SortKey) {
        if (sortBy === field) {
            sortDir = sortDir === "desc" ? "asc" : "desc";
        } else {
            sortBy = field;
            sortDir = field === "name" || field === "role" ? "asc" : "desc";
        }
        onsortchange?.();
    }

    function getSortIndicator(field: SortKey) {
        if (sortBy !== field) return "";
        return sortDir === "desc" ? " ↓" : " ↑";
    }

    function getAriaSort(field: SortKey): "ascending" | "descending" | "none" {
        if (sortBy !== field) return "none";
        return sortDir === "asc" ? "ascending" : "descending";
    }

    /** Pick rate + ban rate (null/sentinel BR contributes 0). Negative when PR is insufficient. */
    function pickBan(h: HeroStats): number {
        const br = h.banRate;
        return h.pickRate + (br === null || br === undefined || br < 0 ? 0 : br);
    }

    const sortedHeroes = $derived.by(() => {
        const list = [...heroes];
        list.sort((a, b) => {
            // Push insufficient-data heroes to the bottom regardless of sort direction
            const aInsuf = isInsufficientData(a.winRate);
            const bInsuf = isInsufficientData(b.winRate);
            if (aInsuf !== bInsuf) return aInsuf ? 1 : -1;

            let cmp: number;
            if (sortBy === "name") cmp = a.name.localeCompare(b.name);
            else if (sortBy === "role") cmp = a.role.localeCompare(b.role);
            else if (sortBy === "tier") cmp = a.tierScore - b.tierScore;
            else if (sortBy === "banRate") {
                // Null/unknown BR sorts as the lowest possible value so it
                // sinks to the bottom in desc order (the default direction).
                const av = a.banRate ?? -Infinity;
                const bv = b.banRate ?? -Infinity;
                cmp = av - bv;
            } else if (sortBy === "pickBan") cmp = pickBan(a) - pickBan(b);
            else cmp = a[sortBy] - b[sortBy];
            return sortDir === "desc" ? -cmp : cmp;
        });
        return list;
    });

    const [winRateMin, winRateMax] = $derived(getStatRange(heroes, "winRate"));
    const [pickRateMin, pickRateMax] = $derived(getStatRange(heroes, "pickRate"));
    const [banRateMin, banRateMax] = $derived(getStatRange(heroes, "banRate"));
    const [pickBanMin, pickBanMax] = $derived.by(() => {
        const vals = heroes.map(pickBan).filter((v) => !isInsufficientData(v));
        if (vals.length === 0) return [0, 1] as [number, number];
        return [Math.min(...vals), Math.max(...vals)] as [number, number];
    });
</script>

<div class="overflow-x-auto">
    <table class="table-pin-cols table text-base-content">
        <thead>
            <tr class="bg-base-300 border-none">
                <th class="bg-base-300 rounded-l-lg"></th>
                <td class="font-normal bg-base-300" aria-sort={getAriaSort("name")}>
                    <button
                        type="button"
                        class="cursor-pointer {sortBy === 'name' ? 'text-primary font-bold' : ''}"
                        onclick={() => toggleSort("name")}>Hero{getSortIndicator("name")}</button
                    >
                </td>
                {#if showRole}
                    <td class="font-normal bg-base-300" aria-sort={getAriaSort("role")}>
                        <button
                            type="button"
                            class="cursor-pointer {sortBy === 'role' ? 'text-primary font-bold' : ''}"
                            onclick={() => toggleSort("role")}>Role{getSortIndicator("role")}</button
                        >
                    </td>
                {/if}
                <td class="font-normal bg-base-300" aria-sort={getAriaSort("tier")}>
                    <button
                        type="button"
                        class="cursor-pointer {sortBy === 'tier' ? 'text-primary font-bold' : ''}"
                        onclick={() => toggleSort("tier")}>Tier{getSortIndicator("tier")}</button
                    >
                </td>
                <td class="font-normal bg-base-300" aria-sort={getAriaSort("winRate")}>
                    <button
                        type="button"
                        class="cursor-pointer {sortBy === 'winRate' ? 'text-primary font-bold' : ''}"
                        onclick={() => toggleSort("winRate")}>Win Rate{getSortIndicator("winRate")}</button
                    >
                </td>
                <td
                    class="font-normal {showBanRate ? '' : 'rounded-r-lg'} bg-base-300"
                    aria-sort={getAriaSort("pickRate")}
                >
                    <button
                        type="button"
                        class="cursor-pointer {sortBy === 'pickRate' ? 'text-primary font-bold' : ''}"
                        onclick={() => toggleSort("pickRate")}>Pick Rate{getSortIndicator("pickRate")}</button
                    >
                </td>
                {#if showBanRate}
                    <td class="font-normal bg-base-300" aria-sort={getAriaSort("banRate")}>
                        <button
                            type="button"
                            class="cursor-pointer {sortBy === 'banRate' ? 'text-primary font-bold' : ''}"
                            onclick={() => toggleSort("banRate")}>Ban Rate{getSortIndicator("banRate")}</button
                        >
                    </td>
                    <td class="font-normal rounded-r-lg bg-base-300" aria-sort={getAriaSort("pickBan")}>
                        <button
                            type="button"
                            class="cursor-pointer {sortBy === 'pickBan' ? 'text-primary font-bold' : ''}"
                            onclick={() => toggleSort("pickBan")}>Pick+Ban{getSortIndicator("pickBan")}</button
                        >
                    </td>
                {/if}
            </tr>
        </thead>
        <tbody class="text-lg font-medium">
            {#each sortedHeroes as hero, i (hero.slug)}
                <tr class="hover">
                    <th class="z-10 p-0 py-1">
                        <a href="/heroes/{hero.slug}{heroLinkSuffix}">
                            <Portrait
                                slug={hero.slug}
                                alt={hero.name}
                                width={70}
                                height={64}
                                priority={i < 8}
                                class="min-w-17.5 rounded"
                            />
                        </a>
                    </th>
                    <td class="bg-base-200 w-full">
                        <a class="link link-hover" href="/heroes/{hero.slug}{heroLinkSuffix}">{hero.name}</a>
                    </td>
                    {#if showRole}
                        <td class="bg-base-200 capitalize">
                            <span class="flex items-center justify-center gap-1">
                                <img
                                    src={getRoleIcon(hero.role)}
                                    alt={hero.role}
                                    width="16"
                                    height="16"
                                    class="inline-block brightness-0 invert"
                                    title={hero.role}
                                />
                            </span>
                        </td>
                    {/if}
                    <td class="bg-base-200">
                        <TierBadge tier={hero.tier} size="lg" />
                    </td>
                    <td class={"bg-base-200 " + getTextColorClass(hero.winRate, winRateMin, winRateMax)}>
                        {formatPercent(hero.winRate)}
                    </td>
                    <td class={"bg-base-200 " + getPickRateColorClass(hero.pickRate, pickRateMin, pickRateMax)}>
                        {formatPercent(hero.pickRate)}
                    </td>
                    {#if showBanRate}
                        <td
                            class={"bg-base-200 " + getBanRateColorClass(hero.banRate, banRateMin, banRateMax)}
                            title={hero.banRate === null ? "No ban data recorded for this period" : undefined}
                        >
                            {formatPercent(hero.banRate)}
                        </td>
                        {@const pb = pickBan(hero)}
                        <td class={"bg-base-200 " + getPickRateColorClass(pb, pickBanMin, pickBanMax)}>
                            {formatPercent(pb)}
                        </td>
                    {/if}
                </tr>
            {/each}
        </tbody>
    </table>
</div>
